import * as Tone from "tone";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";

const SETTINGS_STORAGE_KEY = "bardsCorner.drumPads.settings.v1";
const BASE_MIDI = 36;
const PAD_COUNT = 16;
const PAD_DURATION_SECONDS = 0.55;
const PAD_COLORS = [
  "#c73528", "#d47d28", "#b9a12e", "#62a936",
  "#2d92a8", "#327fc4", "#6d64ca", "#9a58c8",
  "#c46acb", "#c24f90", "#c94868", "#d95d45",
  "#b8673b", "#8fa84f", "#45a284", "#3f8da3",
];
const KEY_BINDINGS = [
  ["KeyZ", "Z"], ["KeyX", "X"], ["KeyC", "C"], ["KeyV", "V"],
  ["KeyA", "A"], ["KeyS", "S"], ["KeyD", "D"], ["KeyF", "F"],
  ["KeyQ", "Q"], ["KeyW", "W"], ["KeyE", "E"], ["KeyR", "R"],
  ["Digit1", "1"], ["Digit2", "2"], ["Digit3", "3"], ["Digit4", "4"],
];
const FULL_DRUM_MIDI_NOTES = Array.from({ length: 47 }, (_unused, index) => 35 + index);
const DRUM_SOUNDFONTS = [
  { id: "drums", label: "Drums", globalName: "drums", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: FULL_DRUM_MIDI_NOTES },
  { id: "casio_vl_1", label: "Casio VL-1", globalName: "drums_casio_vl_1", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: [35, 36, 38, 40, 42, 45, 50] },
  { id: "linn_linndrum", label: "LinnDrum", globalName: "drums_linn_linndrum", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: [35, 36, 37, 38, 39, 42, 44, 45, 46, 47, 49, 50, 51, 54, 56, 63, 64, 69] },
  { id: "roland_606", label: "Roland TR-606", globalName: "drums_roland_606", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: [35, 36, 38, 42, 44, 45, 46, 49, 50] },
  { id: "roland_707", label: "Roland TR-707", globalName: "drums_roland_707", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: [35, 36, 38, 39, 40, 42, 44, 45, 46, 49, 50, 51, 54, 56] },
  { id: "roland_808", label: "Roland TR-808", globalName: "drums_roland_808", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: [35, 36, 38, 39, 40, 41, 42, 44, 45, 46, 47, 48, 49, 50, 51, 54, 55, 56, 57, 59, 70, 75] },
  { id: "roland_909", label: "Roland TR-909", globalName: "drums_roland_909", gain: 0.9, defaultVelocity: 0.86, release: 0.16, availableMidiNotes: [35, 36, 38, 39, 40, 42, 44, 45, 46, 47, 49, 50, 51] },
];
const GM_DRUM_NAMES = {
  36: "Kick", 37: "Side Stick", 38: "Snare", 39: "Clap", 40: "Electric Snare", 41: "Low Floor Tom", 42: "Closed Hat", 43: "High Floor Tom",
  44: "Pedal Hat", 45: "Low Tom", 46: "Open Hat", 47: "Low-Mid Tom", 48: "High-Mid Tom", 49: "Crash", 50: "High Tom", 51: "Ride",
};
const noteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const state = {
  soundBank: "drums",
  samplers: new Map(),
  warmPromise: null,
};

function $(id) {
  return document.getElementById(id);
}

function padMidi(index) {
  return BASE_MIDI + index;
}

function noteName(midi) {
  return `${noteNames[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

function normalizeSoundBank(value, fallback = "drums") {
  const raw = String(value || "").trim();
  return DRUM_SOUNDFONTS.some((kit) => kit.id === raw) ? raw : fallback;
}

function soundfontConfig(id = state.soundBank) {
  return DRUM_SOUNDFONTS.find((kit) => kit.id === normalizeSoundBank(id)) || DRUM_SOUNDFONTS[0];
}

function loadSettings() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SETTINGS_STORAGE_KEY) || "{}");
    state.soundBank = normalizeSoundBank(saved.soundBank);
  } catch {
    state.soundBank = "drums";
  }
}

function saveSettings() {
  window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({ soundBank: state.soundBank }));
}

function soundfontSampleUrlForMidi(soundfont, midi) {
  const targetNote = Tone.Frequency(midi, "midi").toNote();
  if (soundfont[targetNote]) {
    return soundfont[targetNote];
  }

  let closest = null;
  for (const [note, url] of Object.entries(soundfont)) {
    const sampleMidi = Tone.Frequency(note).toMidi();
    if (!Number.isFinite(sampleMidi)) {
      continue;
    }
    const distance = Math.abs(sampleMidi - midi);
    if (!closest || distance < closest.distance) {
      closest = { distance, url };
    }
  }
  return closest?.url || null;
}

function samplerKey(midi) {
  return `${state.soundBank}:${midi}`;
}

async function ensurePadSampler(midi) {
  const key = samplerKey(midi);
  const existing = state.samplers.get(key);
  if (existing?.ready) {
    return existing;
  }
  if (existing?.loadPromise) {
    await existing.loadPromise;
    return existing;
  }

  const config = soundfontConfig();
  const soundfont = window.MIDI?.Soundfont?.[config.globalName];
  if (!soundfont) {
    throw new Error(`Could not load ${config.label}.`);
  }
  const sampleUrl = soundfontSampleUrlForMidi(soundfont, midi);
  if (!sampleUrl) {
    throw new Error(`No sample is available for ${noteName(midi)}.`);
  }

  const output = new Tone.Gain(config.gain).toDestination();
  const samplerState = {
    config,
    output,
    sampler: null,
    ready: false,
    loadPromise: null,
  };
  samplerState.sampler = new Tone.Sampler({
    urls: { [midi]: sampleUrl },
    attack: 0,
    release: config.release,
    onload() {
      samplerState.ready = true;
    },
  }).connect(output);
  samplerState.loadPromise = Tone.loaded().then(() => {
    samplerState.ready = true;
    return samplerState;
  });
  state.samplers.set(key, samplerState);
  await samplerState.loadPromise;
  return samplerState;
}

async function warmPads({ startContext = false } = {}) {
  if (startContext) {
    await Tone.start();
  }
  if (!state.warmPromise) {
    state.warmPromise = Promise.all(Array.from({ length: PAD_COUNT }, (_unused, index) => ensurePadSampler(padMidi(index))))
      .catch((error) => {
        state.warmPromise = null;
        throw error;
      });
  }
  await state.warmPromise;
}

function warmPadsSoon() {
  const warm = () => {
    warmPads().then(() => {
      $("statusText").textContent = `${soundfontConfig().label} ready`;
    }).catch(() => {
      $("statusText").textContent = "Tap a pad to start audio";
    });
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warm, { timeout: 900 });
    return;
  }
  window.setTimeout(warm, 200);
}

function disposeSamplers() {
  state.samplers.forEach((samplerState) => {
    samplerState.sampler?.dispose?.();
    samplerState.output?.dispose?.();
  });
  state.samplers.clear();
  state.warmPromise = null;
}

function flashPad(pad) {
  pad.classList.remove("is-playing");
  void pad.offsetWidth;
  pad.classList.add("is-playing");
  window.setTimeout(() => pad.classList.remove("is-playing"), 130);
}

async function playPad(index) {
  const midi = padMidi(index);
  const pad = document.querySelector(`[data-pad-index="${index}"]`);
  flashPad(pad);
  $("statusText").textContent = `${noteName(midi)} ${GM_DRUM_NAMES[midi] || "Drum"}`;

  try {
    await Tone.start();
    const samplerState = await ensurePadSampler(midi);
    samplerState.sampler?.triggerAttackRelease(
      Tone.Frequency(midi, "midi").toFrequency(),
      PAD_DURATION_SECONDS,
      Tone.now(),
      samplerState.config.defaultVelocity,
    );
    warmPads().catch(() => {
      // The played pad already surfaced errors; the remaining pads can retry on demand.
    });
  } catch (error) {
    $("statusText").textContent = error.message || "Could not play that pad";
  }
}

function stopAll() {
  state.samplers.forEach((samplerState) => samplerState.sampler?.releaseAll(Tone.now()));
  $("statusText").textContent = "Stopped";
}

function renderKitOptions() {
  $("kitSelect").replaceChildren(...DRUM_SOUNDFONTS.map((kit) => {
    const option = document.createElement("option");
    option.value = kit.id;
    option.textContent = kit.label;
    return option;
  }));
  $("kitSelect").value = state.soundBank;
}

function renderPads() {
  const pads = Array.from({ length: PAD_COUNT }, (_unused, visualIndex) => {
    const rowFromTop = Math.floor(visualIndex / 4);
    const column = visualIndex % 4;
    const index = ((3 - rowFromTop) * 4) + column;
    const midi = padMidi(index);
    const button = document.createElement("button");
    button.className = "drum-pad";
    button.type = "button";
    button.dataset.padIndex = String(index);
    button.style.setProperty("--pad-color", PAD_COLORS[index]);
    button.setAttribute("aria-label", `${noteName(midi)} ${GM_DRUM_NAMES[midi] || "Drum"}`);
    button.innerHTML = "<span class=\"pad-key\"></span><span class=\"pad-note\"></span><span class=\"pad-name\"></span>";
    button.querySelector(".pad-key").textContent = KEY_BINDINGS[index][1];
    button.querySelector(".pad-note").textContent = noteName(midi);
    button.querySelector(".pad-name").textContent = GM_DRUM_NAMES[midi] || "Drum";
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      button.setPointerCapture?.(event.pointerId);
      playPad(index);
    });
    return button;
  });
  $("padGrid").replaceChildren(...pads);
}

function renderKeyboardBindings() {
  const rows = [0, 1, 2, 3].map((row) => {
    const wrapper = document.createElement("div");
    wrapper.className = "keyboard-bindings-row";
    KEY_BINDINGS.slice(row * 4, row * 4 + 4).forEach((binding, column) => {
      const key = binding[1];
      const index = row * 4 + column;
      const midi = padMidi(index);
      const chip = document.createElement("span");
      chip.className = "binding-chip";
      chip.innerHTML = "<kbd></kbd><span></span>";
      chip.querySelector("kbd").textContent = key;
      chip.querySelector("span").textContent = noteName(midi);
      wrapper.append(chip);
    });
    return wrapper;
  }).reverse();
  $("keyboardBindings").replaceChildren(...rows);
}

function setKitFromInput() {
  state.soundBank = normalizeSoundBank($("kitSelect").value, state.soundBank);
  playSoundfontPreview("drums", state.soundBank);
  saveSettings();
  disposeSamplers();
  $("statusText").textContent = `Loading ${soundfontConfig().label}`;
  warmPadsSoon();
}

function bindEvents() {
  $("kitSelect").addEventListener("change", setKitFromInput);
  $("panicButton").addEventListener("click", stopAll);
  window.addEventListener("keydown", (event) => {
    if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const index = KEY_BINDINGS.findIndex(([code]) => code === event.code);
    if (index === -1) {
      return;
    }
    event.preventDefault();
    playPad(index);
  });
}

loadSettings();
renderKitOptions();
renderPads();
renderKeyboardBindings();
bindEvents();
warmPadsSoon();
